'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useRef } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import LIGHT_COLORS from '../core/LightColors';
import MEDIUM_COLORS from '../core/MediumColors';
import NORMAL_COLORS from '../core/NormalColors';
import { uniqueId } from '../utils/underscore';
import passthroughProps from '../utils/propTypes/passthroughProps';
import { toPx } from '../utils/Styles';
import UICloseButton from '../button/UICloseButton';
import UIClickable from '../button/UIClickable';
import UILink from '../link/UILink';
import UITruncateString from '../text/UITruncateString';
import { EERIE, GYPSUM, OLAF } from 'HubStyleTokens/colors';
import { WEB_FONT_DEMI_BOLD_WEIGHT, WEB_FONT_MEDIUM_WEIGHT } from 'HubStyleTokens/misc';
import { BASE_SPACING_X, TAG_ICON_SPACING_X, TAG_FONT_SIZE, TAG_HEIGHT, TAG_PADDING_X, TAG_PADDING_Y } from 'HubStyleTokens/sizes';
import { BASE_FONT_COLOR } from 'HubStyleTokens/theme';
const USE_CLASSES = {
  // These do not come from the sketch file
  default: 'tag-default private-tag--default',
  primary: 'tag-primary',
  info: 'tag-info private-tag--info',
  success: 'tag-success private-tag--success',
  warning: 'tag-warning private-tag--warning',
  danger: 'tag-danger private-tag--danger',
  calypso: '',
  sorbet: '',
  lorax: '',
  marigold: '',
  'candy-apple': '',
  norman: '',
  thunderdome: '',
  oz: '',
  olaf: ''
};
const STATUS_ALIAS_MAP = {
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  active: 'success',
  sent: 'success',
  published: 'success',
  inReview: 'warning',
  scheduled: 'warning',
  removed: 'danger',
  cancelled: 'danger',
  error: 'danger'
};
const USE_COLOR_ALIAS_MAP = {
  // defaults
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  // oz
  success: 'oz',
  active: 'oz',
  sent: 'oz',
  published: 'oz',
  // marigold
  warning: 'marigold',
  inReview: 'marigold',
  scheduled: 'marigold',
  // cand-apple
  danger: 'candy-apple',
  removed: 'candy-apple',
  cancelled: 'candy-apple',
  error: 'candy-apple',
  // calypso
  info: 'calypso'
};
const TAG_BACKGROUND_COLORS = Object.assign({}, LIGHT_COLORS, {
  default: GYPSUM
});
const TAG_IS_BORDERED_BORDER_COLORS = Object.assign({}, MEDIUM_COLORS, {
  default: EERIE
});
const TAG_BORDER_COLORS = Object.assign({}, NORMAL_COLORS, {
  default: EERIE
});

const getBackgroundColor = use => {
  return TAG_BACKGROUND_COLORS[use] != null ? TAG_BACKGROUND_COLORS[use] : EERIE;
};

const getBorderColor = (use, bordered) => {
  if (bordered && TAG_IS_BORDERED_BORDER_COLORS[use] != null) {
    return TAG_IS_BORDERED_BORDER_COLORS[use];
  } else if (TAG_BORDER_COLORS[use] != null) {
    return TAG_BORDER_COLORS[use];
  }

  return 'transparent';
};

const getInlineStyles = inline => {
  return inline ? `
        margin: 0 4px;
        vertical-align: middle;
      ` : null;
};

const getCloseablePadding = closeable => {
  return closeable ? `
        padding-right: 28px !important;
      ` : null;
};

const Tag = styled.span.withConfig({
  displayName: "UITag__Tag",
  componentId: "txxrfz-0"
})(["font-size:", ";background-color:", ";border:1px solid ", ";color:", ";line-height:", ";padding:", " ", ";", " position:relative;cursor:inherit;display:inline-flex;font-weight:", ";max-width:100%;overflow:hidden;user-select:none;vertical-align:8px;> * + *{margin-left:", ";}& + .private-tag{margin-left:", ";}a,a:hover,a:active{color:inherit;}", ""], TAG_FONT_SIZE, props => getBackgroundColor(props.computedUse), props => getBorderColor(props.computedUse, props._bordered), props => props.computedUse === 'olaf' ? OLAF : BASE_FONT_COLOR, toPx(parseInt(TAG_HEIGHT, 10) - 2), TAG_PADDING_Y, TAG_PADDING_X, props => getCloseablePadding(props.closeable), WEB_FONT_DEMI_BOLD_WEIGHT, TAG_ICON_SPACING_X, ({
  inline
}) => inline ? null : BASE_SPACING_X, props => getInlineStyles(props.inline));
const FlexWrapper = styled.span.withConfig({
  displayName: "UITag__FlexWrapper",
  componentId: "txxrfz-1"
})(["display:inline-flex;max-width:100%;vertical-align:top;"]);
const ContentWrapper = styled.span.withConfig({
  displayName: "UITag__ContentWrapper",
  componentId: "txxrfz-2"
})(["overflow:hidden;"]);
const StyledCloseButton = styled(UICloseButton).withConfig({
  displayName: "UITag__StyledCloseButton",
  componentId: "txxrfz-3"
})(["bottom:0;color:currentColor;margin-bottom:auto;margin-top:auto;position:absolute;right:6px;top:0;transform:scale(0.5);"]);
const StyledLink = styled(UILink).withConfig({
  displayName: "UITag__StyledLink",
  componentId: "txxrfz-4"
})(["overflow:hidden;"]);
StyledLink.displayName = 'StyledUILink';
const LinkStyledClickable = styled(UIClickable).withConfig({
  displayName: "UITag__LinkStyledClickable",
  componentId: "txxrfz-5"
})(["&&{font-weight:", ";overflow:hidden;text-decoration:underline;}"], WEB_FONT_MEDIUM_WEIGHT);
LinkStyledClickable.displayName = 'StyledUIClickable';

const renderContents = (href, target, onClick, truncatedChildren) => {
  const buttonClassName = 'private-tag__link';

  if (href) {
    return /*#__PURE__*/_jsx(StyledLink, {
      className: buttonClassName,
      href: href,
      onClick: onClick,
      target: target,
      use: "on-bright",
      children: truncatedChildren
    });
  } else if (onClick) {
    return /*#__PURE__*/_jsx(LinkStyledClickable, {
      className: buttonClassName,
      onClick: onClick,
      children: truncatedChildren
    });
  }

  return /*#__PURE__*/_jsx(ContentWrapper, {
    className: "private-tag__content-wrapper",
    children: truncatedChildren
  });
};

let hasPingedNewRelic = false;
export default function UITag(props) {
  const {
    _avatar,
    _bordered,
    children,
    className,
    closeable,
    href,
    id,
    inline,
    multiline,
    onClick,
    onCloseClick,
    _onCloseMouseDown,
    target,
    truncateStringProps,
    use,
    _flexWrapperProps
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["_avatar", "_bordered", "children", "className", "closeable", "href", "id", "inline", "multiline", "onClick", "onCloseClick", "_onCloseMouseDown", "target", "truncateStringProps", "use", "_flexWrapperProps"]);

  const idRef = useRef(uniqueId('tag-'));
  const computedId = id || idRef.current;
  const truncatedChildren = multiline ? children : /*#__PURE__*/_jsx(UITruncateString, Object.assign({
    useFlex: true
  }, truncateStringProps, {
    children: children
  }));

  if (STATUS_ALIAS_MAP[use]) {
    if (window.newrelic && !hasPingedNewRelic) {
      hasPingedNewRelic = true;
      window.newrelic.addPageAction('uitag-use-alias', {
        component: 'UITag',
        alias: use
      });
    }
  }

  return /*#__PURE__*/_jsxs(Tag, Object.assign({
    className: classNames("tag private-tag", className, USE_CLASSES[STATUS_ALIAS_MAP[use] || use], _bordered && 'private-tag--bordered', closeable && 'private-tag--has-close', inline && 'private-tag--inline'),
    "data-component-name": "UITag",
    id: computedId,
    computedUse: USE_COLOR_ALIAS_MAP[use] || use,
    _bordered: _bordered,
    closeable: closeable,
    inline: inline
  }, rest, {
    children: [/*#__PURE__*/_jsxs(FlexWrapper, Object.assign({
      className: "private-tag__flex-wrapper"
    }, _flexWrapperProps, {
      children: [_avatar, renderContents(href, target, onClick, truncatedChildren)]
    })), closeable && /*#__PURE__*/_jsx(StyledCloseButton, {
      "aria-describedby": computedId,
      className: "private-tag__close",
      onClick: onCloseClick,
      onMouseDown: _onCloseMouseDown
    })]
  }));
}
UITag.propTypes = {
  /**
   * The content for the tag
   */
  children: PropTypes.any,

  /**
   * Renders a close button inside the tag. Use `onCloseClick` to provide close behavior.
   */
  closeable: PropTypes.bool.isRequired,

  /**
   * A URL that the tag will link to
   */
  href: PropTypes.string,

  /**
   * Set where you want the linked URL to display. This works as it would on an anchor element.
   */
  target: PropTypes.string,

  /**
   * Adds styles to ensure `UITag` aligns correctly within text.
   */
  inline: PropTypes.bool.isRequired,

  /**
   * When enabled, allows the content to wrap to a newline instead of being truncated
   */
  multiline: PropTypes.bool.isRequired,

  /**
   * Add click behavior directly to the `UITag`. Automatically maps to `onKeyDown` to provide keyboard support.
   */
  onClick: PropTypes.func,

  /**
   * Function that is called when the close button is clicked. Requires `closeable={true}`.
   */
  onCloseClick: PropTypes.func,

  /**
   * Props that are passed down to the rendered `UITruncateString`. Does not work with `multiline={true}` since truncation is disabled.
   */
  truncateStringProps: passthroughProps(UITruncateString),

  /**
   * Sets the color theme to be used for the tag's border and background color.
   */
  use: PropTypes.oneOf(Object.keys(USE_CLASSES).concat(Object.keys(STATUS_ALIAS_MAP))).isRequired,

  /**
   * Slot to render an avatar next to the content. Used internally by `UIAvatarTag`.
   */
  _avatar: PropTypes.node,
  // for UIAvatarTag

  /**
   * Use a lighter border color, if supported by the `use` prop. If not supported the standard border color is used. Used by MultiSelects to avoid clashing borders.
   */
  _bordered: PropTypes.bool,
  // for MultiSelect

  /**
   * Set `onMouseDown` behavior to the close button rendered by `closeable`. Used internally by `UISelect` with `multi={true}` to prevent input focus.
   */
  _onCloseMouseDown: PropTypes.func,
  // for MultiSelectValue

  /**
   * Props passed down to the flex wrapper around `UIStatusTag`
   */
  _flexWrapperProps: PropTypes.object // for UIStatusTag

};
UITag.defaultProps = {
  _bordered: false,
  closeable: false,
  inline: false,
  multiline: false,
  use: 'default'
};
UITag.displayName = 'UITag';
UITag.STATUS_ALIAS_MAP = STATUS_ALIAS_MAP;